import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'

interface FormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  icon?: ReactNode
  /** id of the <form> rendered as children; the footer's submit button targets it. */
  formId: string
  submitLabel: string
  pending: boolean
  tone?: 'primary' | 'danger'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  children: ReactNode
}

/** Modal wrapper for a finance form with a Cancel / Submit footer. */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  formId,
  submitLabel,
  pending,
  tone = 'primary',
  size,
  children,
}: FormDialogProps) {
  const { t } = useTranslation('finance')
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      icon={icon}
      size={size}
      dismissible={!pending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            form={formId}
            loading={pending}
            variant={tone === 'danger' ? 'danger' : 'primary'}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  )
}
